import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight, HelpCircle } from 'lucide-react';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'What is Print on Demand and how does it work?',
      answer: 'Print on Demand (POD) is a fulfillment method where products are printed and shipped only after a customer places an order. This means you don\'t need to hold inventory or invest upfront in products. When you receive an order, it\'s automatically sent to us, we print the design on the product, perform quality checks, and ship directly to your customer.'
    },
    {
      question: 'What are your shipping times and delivery options?',
      answer: 'Our standard processing time is 1-3 business days for most products. Shipping times vary depending on the destination: Domestic US orders typically arrive in 3-7 business days with standard shipping, or 1-3 days with express shipping. International orders usually take 7-21 business days.'
    },
    {
      question: 'Do you offer custom branding and packaging?',
      answer: 'Yes! We offer comprehensive custom branding solutions to help you create a memorable unboxing experience. This includes custom packaging boxes, branded tissue paper, custom packing slips and invoices, thank you cards and promotional inserts, branded shipping labels, and custom tape and stickers.'
    },
    {
      question: 'What ecommerce platforms do you integrate with?',
      answer: 'We integrate seamlessly with all major ecommerce platforms including Shopify, WooCommerce, Etsy, Amazon, eBay, BigCommerce, Wix, Squarespace, and TikTok Shop. Our API also allows for custom integrations with any platform.'
    },
    {
      question: 'What is your minimum order quantity?',
      answer: 'We have no minimum order quantity for most of our services. Whether you\'re selling one product or thousands, we\'re here to help. For Print on Demand, each order is printed individually, so there\'s truly no minimum. For bulk orders, we offer volume discounts starting at 100+ units.'
    },
    {
      question: 'How do returns and exchanges work?',
      answer: 'We handle returns and exchanges to make the process seamless. If a product is defective or damaged during shipping, we\'ll replace it at no cost. For customer-initiated returns, we can process returns to our warehouse and notify you of the condition. Returned items in good condition can be restocked or reshipped.'
    },
    {
      question: 'What quality control measures do you have?',
      answer: 'Quality is our top priority. Every order goes through a multi-step quality control process: We verify order details, check print quality and color accuracy, inspect products for defects before packaging, and verify correct packaging and shipping labels. Our accuracy rate is 99.5%.'
    },
    {
      question: 'How do I get started with Granite Current?',
      answer: 'Getting started is easy! Simply contact us through our website or email support@Granitecurrent.com. We\'ll schedule a consultation call to understand your needs and recommend the best solutions. Once ready, we\'ll help you integrate your store and guide you through the setup. Most businesses are up and running within 24-48 hours.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div data-testid="faq-page" className="bg-[#fffdf8]">
      {/* Hero */}
      <section className="pt-32 pb-20 warm-gradient">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-[#ea580c] text-sm font-medium tracking-wider uppercase mb-4 block">
              FAQ
            </span>
            <h1 className="font-['Cormorant_Garamond'] text-5xl md:text-6xl lg:text-7xl font-semibold text-[#78350f] mb-6" data-testid="faq-hero-title">
              Frequently Asked
              <br />
              <span className="text-[#ea580c]">Questions</span>
            </h1>
            <p className="text-[#78350f]/80 text-lg">
              Find answers to common questions about our fulfillment services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="bg-[#fef3c7]/50 overflow-hidden organic-shape-3"
                data-testid={`faq-item-${index}`}
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  data-testid={`faq-toggle-${index}`}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-[#fef3c7] transition-colors"
                >
                  <span className="font-['Cormorant_Garamond'] font-semibold text-[#78350f] pr-4 text-xl">{faq.question}</span>
                  <ChevronDown 
                    size={20} 
                    className={`text-[#ea580c] flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`}
                  />
                </button>
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="px-6 pb-6 text-[#78350f]/80 leading-relaxed border-t border-[#fcdcae] pt-4">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-[#fef3c7]/30" data-testid="contact-cta-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white p-10 md:p-16 organic-shape-1 warm-shadow"
          >
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="w-14 h-14 bg-[#ea580c] rounded-2xl flex items-center justify-center mb-6">
                  <HelpCircle size={28} className="text-white" />
                </div>
                <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-semibold text-[#78350f] mb-4">
                  Still Have Questions?
                </h2>
                <p className="text-[#78350f]/80 mb-8">
                  Can't find the answer you're looking for? Our warm team is ready to 
                  assist you with any questions about our services.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link
                    to="/contact"
                    data-testid="faq-cta-contact"
                    className="bg-[#ea580c] text-white px-6 py-3 font-medium hover:bg-[#c2410c] transition-all duration-300 hover:translate-y-[-2px] hover:shadow-lg inline-flex items-center gap-2 group organic-shape-3"
                  >
                    Contact Support
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <a
                    href="mailto:support@Granitecurrent.com"
                    data-testid="faq-email-link"
                    className="bg-[#fef3c7] text-[#78350f] px-6 py-3 font-medium hover:bg-[#fcdcae] transition-colors organic-shape-3"
                  >
                    Email Us
                  </a>
                </div>
              </div>
              <div className="hidden md:block">
                <div className="organic-shape-2 overflow-hidden">
                  <img
                    src="https://images.pexels.com/photos/5699456/pexels-photo-5699456.jpeg?auto=compress&cs=tinysrgb&w=600"
                    alt="Customer support"
                    className="w-full aspect-[4/3] object-cover"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 bg-[#fffdf8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { title: 'Our Services', description: 'Explore fulfillment solutions', link: '/services' },
              { title: 'About Us', description: 'Learn about our mission', link: '/about' },
              { title: 'Get Started', description: 'Ready to partner with us?', link: '/contact' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={item.link}
                  data-testid={`quick-link-${index}`}
                  className="block bg-[#fef3c7]/50 p-6 hover:bg-[#fef3c7] transition-all duration-300 hover:translate-y-[-4px] hover:shadow-lg group organic-shape-3"
                >
                  <h3 className="font-['Cormorant_Garamond'] font-semibold text-xl text-[#78350f] mb-2 group-hover:text-[#ea580c] transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-[#78350f]/60 text-sm mb-3">{item.description}</p>
                  <span className="inline-flex items-center text-[#ea580c] text-sm font-medium">
                    Learn More
                    <ArrowRight size={14} className="ml-1 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
